import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Ticket, Paperclip, Send, Image as ImageIcon } from "lucide-react";
import { useLocation } from "react-router-dom";

const STATUS = { aberto: ["Aberto", "border-amber-700 text-amber-200 bg-amber-950/30"], andamento: ["Em andamento", "border-sky-700 text-sky-200 bg-sky-950/30"], resolvido: ["Resolvido", "border-green-700 text-green-200 bg-green-950/30"] };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "");

function Pill({ s }) {
  const [label, cls] = STATUS[s] || ["", ""];
  return <span data-testid={`ticket-status-${s}`} className={`inline-flex items-center h-6 px-2 rounded-full text-[11px] font-bold uppercase border ${cls}`}>{label}</span>;
}

function CreateDialog({ open, onClose, onCreated, defaultScreen }) {
  const [f, setF] = useState(() => {
    const sp = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
    return { subject: sp?.get("subject") || "", description: sp?.get("description") || "", screen: defaultScreen || sp?.get("screen") || "" };
  });
  const [img, setImg] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { if (open) { setF({ subject: "", description: "", screen: defaultScreen || "" }); setImg(null); } }, [open, defaultScreen]);
  const pick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 300 * 1024) { toast.error("Imagem muito grande (máx. 300 KB)"); return; }
    const buf = await file.arrayBuffer();
    const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
    setImg(`data:${file.type};base64,${b64}`);
  };
  const submit = async () => {
    if (!f.subject.trim() || !f.description.trim()) { toast.error("Preencha assunto e descrição"); return; }
    setSaving(true);
    try { const { data } = await api.post("/support/tickets", { ...f, screenshot: img }); onCreated(data); toast.success("Chamado aberto"); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ticket-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Abrir chamado</DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">O suporte responde dentro do próprio app e também recebe um e-mail.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input data-testid="ticket-subject" placeholder="Assunto" value={f.subject} onChange={(e) => setF((p) => ({ ...p, subject: e.target.value }))} className="h-11 bg-zinc-900 border-zinc-800" />
          <Textarea data-testid="ticket-desc" placeholder="Descreva o que aconteceu, o que esperava e o que apareceu" value={f.description} onChange={(e) => setF((p) => ({ ...p, description: e.target.value }))} className="min-h-[120px] bg-zinc-900 border-zinc-800" />
          <div className="grid grid-cols-2 gap-2">
            <input ref={fileRef} type="file" accept="image/*" onChange={pick} className="hidden" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="ticket-attach" className="h-10 bg-zinc-900 border-zinc-800 text-xs"><Paperclip className="h-4 w-4 mr-1.5" /> {img ? "Imagem anexada" : "Anexar print"}</Button>
            {img && <img alt="Prévia" src={img} className="h-10 w-full object-cover rounded border border-zinc-800" />}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="bg-zinc-900 border-zinc-800">Cancelar</Button>
          <Button data-testid="ticket-submit" onClick={submit} disabled={saving} className="bg-green-brand hover:bg-green-brandDark text-white font-bold">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enviar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Thread({ t, onChanged }) {
  const [open, setOpen] = useState(false);
  const [img, setImg] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { if (!open || !t.has_screenshot || img) return; api.get(`/support/tickets/${t.id}/screenshot`).then(({ data }) => setImg(data.data_url)).catch(() => {}); }, [open, t.id, t.has_screenshot, img]);
  const send = async () => {
    const text = reply.trim(); if (!text) return;
    setSending(true);
    try { const { data } = await api.post(`/support/tickets/${t.id}/reply`, { text }); setReply(""); onChanged(data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };
  return (
    <div data-testid={`ticket-${t.id}`} className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-left min-w-0 flex-1">
          <div className="font-display text-base font-bold text-zinc-100 truncate">{t.subject}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">{fmt(t.created_at)} · {t.screen || "sem tela"}</div>
        </button>
        <Pill s={t.status} />
      </div>
      {open && (
        <div className="space-y-2 pt-2 border-t border-zinc-800">
          <p className="text-sm text-zinc-200 whitespace-pre-wrap">{t.description}</p>
          {t.has_screenshot && (img ? <img src={img} alt="anexo" className="max-h-48 rounded border border-zinc-800" /> : <div className="text-[11px] text-zinc-500 flex items-center gap-1"><ImageIcon className="h-3 w-3" /> carregando anexo…</div>)}
          <div className="space-y-2">
            {t.messages?.map((m, i) => (
              <div key={i} data-testid={`ticket-msg-${t.id}-${i}`} className={`rounded-lg p-2.5 text-sm ${m.by === "admin" ? "bg-emerald-950/40 border border-emerald-800/50" : "bg-zinc-900 border border-zinc-800"}`}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{m.by === "admin" ? "Suporte" : "Você"} · {fmt(m.created_at)}</div>
                <p className="text-zinc-100 whitespace-pre-wrap mt-0.5">{m.text}</p>
              </div>
            ))}
          </div>
          {t.status !== "resolvido" && (
            <div className="flex items-center gap-2 pt-1">
              <Input data-testid={`ticket-reply-${t.id}`} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Escreva uma resposta…" className="h-10 bg-zinc-900 border-zinc-800" />
              <Button data-testid={`ticket-reply-send-${t.id}`} onClick={send} disabled={sending || !reply.trim()} className="h-10 bg-green-brand hover:bg-green-brandDark text-white"><Send className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function OwnerSupport() {
  const loc = useLocation();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const load = async () => { try { const { data } = await api.get("/support/tickets"); setTickets(data); } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const change = (t) => setTickets((p) => p.map((x) => (x.id === t.id ? t : x)));
  return (
    <div data-testid="owner-support" className="space-y-5 animate-fade-up">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] font-mono-caps text-zinc-500">Suporte</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100">Chamados</h1>
          <p className="text-sm text-zinc-400 mt-1">Fale pelo WhatsApp pelo menu, ou abra um chamado aqui para registrar.</p>
        </div>
        <Button data-testid="ticket-new-btn" onClick={() => setDialog(true)} className="h-11 bg-green-brand hover:bg-green-brandDark text-white font-bold"><Plus className="h-4 w-4 mr-2" /> Abrir chamado</Button>
      </div>
      <CreateDialog open={dialog} onClose={() => setDialog(false)} onCreated={(t) => setTickets((p) => [t, ...p])} defaultScreen={loc.state?.screen} />
      {loading ? <Loader2 className="h-6 w-6 animate-spin text-green-brand" /> : tickets.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 p-10 text-center">
          <Ticket className="h-10 w-10 mx-auto text-zinc-700" />
          <p className="mt-3 text-zinc-400">Nenhum chamado ainda.</p>
        </div>
      ) : <div className="space-y-2">{tickets.map((t) => <Thread key={t.id} t={t} onChanged={change} />)}</div>}
    </div>
  );
}
